import { Fragment, useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useRouter, Link } from '@tanstack/react-router';
import { Send, Paperclip, X, FileText, Loader2, MessageSquare, Trash2, Ban } from '@/components/icons';
import { AutosizeTextarea } from './ui/autosize-textarea';
import { DetailSkeleton, ListSkeleton } from './ui/skeleton-presets';
import { Avatar, AvatarFallback } from './ui/avatar';
import { AttachmentPreview } from './AttachmentPreview';
import { EmptyState } from './EmptyState';
import { TopBarChat } from './topbar/TopBarChat';
import { toast } from 'sonner';
import { track, EVENTS } from '@/utils/analytics';
import { useConversation, useMessages, useSendMessage, useGenerateUploadUrl, useMarkConversationAsRead, useDeleteMessage } from '../lib/convex-hooks';
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember';
import { useCrewGetAll } from '../lib/hooks';
import { useNotifyNewMessage } from '../lib/hooks/message';
import type { Id } from '@pronteer/convex/dataModel';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Button } from './ui/button';
import { SwipeableRow } from './ui/swipeable-row';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useIsDesktop } from './ui/use-mobile';
import { useKeyboardHeight } from '@/lib/keyboard';
import { cn } from '@/lib/utils';
import { makeThumbnailFile, resizeImageFile } from '@/lib/resize-image';
import { useStickToBottom } from '@/lib/hooks/useStickToBottom';

dayjs.extend(relativeTime);

// Longest edge for chat photos; the 400px grid thumbnail is made separately.
const CHAT_IMAGE_MAX_DIMENSION = 1600;

interface ConversationViewProps {
  conversationId: Id<'conversations'>
}

export function ConversationView({
  conversationId,
}: ConversationViewProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const { data: currentUser } = useCurrentCrewMember();
  const conversation = useConversation(conversationId, currentUser?.id);
  const { data: crewMembers = [] } = useCrewGetAll() as { data: any[] };

  // Load all messages for the conversation at once (oldest first)
  const convexMessages = useMessages(conversationId, currentUser?.id);
  const isLoading = convexMessages === undefined;

  const sendMessage = useSendMessage();
  const generateUploadUrl = useGenerateUploadUrl();
  const markAsRead = useMarkConversationAsRead();
  const deleteMessage = useDeleteMessage();
  const { mutateAsync: notifyNewMessage } = useNotifyNewMessage();


  const [messageText, setMessageText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Swipe-to-delete: only one row open at a time, delete behind a confirmation.
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<Id<'messages'> | null>(null);
  const [deletingId, setDeletingId] = useState<Id<'messages'> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Keyboard.resize is "none": the webview doesn't shrink for the keyboard,
  // so the composer lifts itself above it (margin below).
  const keyboardHeight = useKeyboardHeight();

  // Pins the thread to the newest message: on open, as messages arrive, and
  // whenever the list shrinks (keyboard lift, growing composer) or grows
  // (attachments finishing load). Releases as soon as you scroll up.
  const { containerRef, contentRef, onScroll, isAtBottom, stickToBottom } =
    useStickToBottom<HTMLDivElement>(conversationId);

  // Mark conversation as read when viewed
  useEffect(() => {
    if (conversationId && currentUser?.id) {
      markAsRead({ conversationId, crewId: currentUser.id });
    }
  }, [conversationId, currentUser?.id, markAsRead]);

  // Mark as read when new messages arrive and user is at bottom
  useEffect(() => {
    if (conversationId && currentUser?.id && convexMessages && convexMessages.length > 0 && isAtBottom) {
      markAsRead({ conversationId, crewId: currentUser.id });
    }
  }, [conversationId, currentUser?.id, convexMessages?.length, isAtBottom, markAsRead]);

  const handleSend = async () => {
    const content = messageText.trim();
    if (!content && selectedFiles.length === 0) return;

    setIsSending(true);
    try {
      let attachments:
        | { storageId: Id<'_storage'>; thumbStorageId?: Id<'_storage'>; type: string; name: string }[]
        | undefined;

      // Upload files if present
      if (selectedFiles.length > 0) {
        try {
          setIsUploading(true);

          // Images are downscaled on the device (max 1600px, like other
          // messengers) and get a 400px thumbnail for the bubble grid so a
          // chat never downloads 5MB camera originals. Other files go as-is.
          const prepared = await Promise.all(
            selectedFiles.map(async (file) => {
              if (!file.type.startsWith('image/')) return { original: file, thumb: null };
              const [original, thumb] = await Promise.all([
                resizeImageFile(file, CHAT_IMAGE_MAX_DIMENSION),
                makeThumbnailFile(file, 'thumb'),
              ]);
              return { original, thumb };
            })
          );

          const uploadBlob = async (uploadUrl: string, blob: File) => {
            const uploadResponse = await fetch(uploadUrl, {
              method: 'POST',
              headers: { 'Content-Type': blob.type },
              body: blob,
            });
            if (!uploadResponse.ok) {
              throw new Error(`Upload failed: ${uploadResponse.statusText}`);
            }
            const { storageId } = await uploadResponse.json();
            return storageId as Id<'_storage'>;
          };

          // Generate all upload URLs sequentially first (Convex mutations can't run concurrently)
          const uploadUrls: { original: string; thumb: string | null }[] = [];
          for (const { thumb } of prepared) {
            uploadUrls.push({
              original: await generateUploadUrl(),
              thumb: thumb ? await generateUploadUrl() : null,
            });
          }

          // Then upload all files in parallel
          const uploaded = await Promise.all(
            prepared.map(async ({ original, thumb }, i) => {
              const [storageId, thumbStorageId] = await Promise.all([
                uploadBlob(uploadUrls[i].original, original),
                thumb && uploadUrls[i].thumb ? uploadBlob(uploadUrls[i].thumb, thumb) : Promise.resolve(undefined),
              ]);
              return { storageId, thumbStorageId, type: original.type, name: original.name };
            })
          );
          attachments = uploaded;
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          toast.error('Failed to upload files');
          return;
        } finally {
          setIsUploading(false);
        }
      }

      // Send message
      await sendMessage({
        conversationId: conversationId,
        crewId: currentUser!.id,
        senderName: currentUser!.name,
        senderAvatarColor: currentUser!.avatarColor,
        content,
        attachments,
      });

      // Fire a native push to the other participants (best-effort, never blocks
      // the chat flow). Skips anyone who muted this conversation; the API further
      // gates on each recipient's notifyChat preference.
      const recipientCrewIds = (conversation?.otherParticipants ?? [])
        .filter((p: any) => !p.isMuted)
        .map((p: any) => p.crewId as string);
      if (recipientCrewIds.length > 0) {
        void notifyNewMessage({
          conversationId,
          recipientCrewIds,
          senderName: currentUser!.name,
          content,
          hasAttachments: (attachments?.length ?? 0) > 0,
          isGroup: conversation?.isGroup ?? false,
          conversationName: conversation?.name,
        }).catch((err) => console.error('Failed to send message push:', err));
      }

      track(EVENTS.MESSAGE_SENT, { hasAttachments: (attachments?.length ?? 0) > 0 })
      setMessageText('');
      setSelectedFiles([]);
      stickToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    const allFiles = [...selectedFiles, ...newFiles];
    const totalSize = allFiles.reduce((sum, f) => sum + f.size, 0);

    if (totalSize > 10 * 1024 * 1024) {
      toast.error('Total file size must be less than 10MB');
      return;
    }

    setSelectedFiles(allFiles);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Soft delete — the bubble stays but reads "Message deleted". The Convex
  // mutation also enforces that only the sender can delete.
  const handleConfirmDelete = async () => {
    if (!confirmDeleteId || !currentUser) return;
    const messageId = confirmDeleteId;
    setDeletingId(messageId);
    try {
      await deleteMessage({ messageId, crewId: currentUser.id });
      setConfirmDeleteId(null);
      setOpenRowId(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    } finally {
      setDeletingId(null);
    }
  };

  // Compute display name for DMs (use other participant's name)
  // Must be before early return to maintain hook order
  const displayName = useMemo(() => {
    if (!conversation) return '';

    // For groups, use the conversation name
    if (conversation.isGroup) {
      return conversation.name;
    }

    // For DMs, look up the other participant's name
    if (conversation.otherParticipants && conversation.otherParticipants.length > 0) {
      const otherParticipantCrewId = conversation.otherParticipants[0].crewId;
      const crewMember = crewMembers.find((cm: any) => cm.id === otherParticipantCrewId);

      if (crewMember) {
        return crewMember.firstName && crewMember.lastName
          ? `${crewMember.firstName} ${crewMember.lastName}`
          : crewMember.name || 'Unknown';
      }
    }

    // Fallback to conversation name
    return conversation.name || 'Unknown';
  }, [conversation, crewMembers]);

  // Get avatar color for DM participant
  const participantAvatarColor = useMemo(() => {
    if (!conversation || conversation.isGroup) return undefined;
    if (conversation.otherParticipants && conversation.otherParticipants.length > 0) {
      const otherParticipantCrewId = conversation.otherParticipants[0].crewId;
      const crewMember = crewMembers.find((cm: any) => cm.id === otherParticipantCrewId);
      return crewMember?.avatarColor ?? undefined;
    }
    return undefined;
  }, [conversation, crewMembers]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Loading state
  if (!conversation || !currentUser) {
    return (
      <div className="fixed inset-0 bg-surface-L0 z-60 overflow-y-auto">
        <DetailSkeleton />
      </div>
    );
  }


  const formatTime = (timestamp: number) => {
    const messageTime = dayjs(timestamp);
    return messageTime.fromNow();
  };

  // Format messages from Convex (query returns oldest first)
  const formattedMessages = convexMessages
    ?.map((msg) => ({
      id: msg._id,
      senderId: msg.senderId,
      senderName: msg.senderName,
      senderInitials: msg.senderName?.split(' ').map((n: string) => n[0]).join('').substring(0, 2) || '??',
      senderAvatarColor: msg.senderAvatarColor || '#891038',
      content: msg.content,
      timestamp: msg.createdAt,
      isCurrentUser: msg.senderId === currentUser.id,
      // `isDeleted` is only set on messages deleted after the flag was added —
      // fall back to the sentinel content older soft deletes left behind.
      isDeleted: msg.isDeleted === true || msg.content === 'Message deleted',
      attachmentStorageId: msg.attachmentStorageId,
      attachmentType: msg.attachmentType,
      attachmentName: msg.attachmentName,
      attachments: msg.attachments,
    })) || [];

  return (
    <div className="flex flex-col h-full w-full bg-surface-brand-L0">
      {/* Header */}
      {isDesktop ? (
        <header className="flex flex-col gap-2 border-b border-border-L1 px-8 py-5">
          <div className="text-sm font-medium text-content-brand-bold">
            <Link to="/conversations" className="hover:underline">
              Messages
            </Link>
            <span className="text-content-primary-subtle"> / {displayName}</span>
          </div>
          <div className="flex items-center gap-3">
            <Avatar className="size-9">
              <AvatarFallback
                className="text-sm font-semibold uppercase text-background"
                style={participantAvatarColor ? { backgroundColor: participantAvatarColor } : undefined}
              >
                {conversation.isGroup
                  ? displayName?.substring(0, 2).toUpperCase() || '??'
                  : displayName?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || '??'}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <h1 className="text-lg font-bold leading-tight tracking-[-0.5px] text-content-primary-bold">
                {displayName}
              </h1>
              {conversation.isGroup && (
                <p className="text-xs text-content-primary-subtle">
                  {conversation.participantIds?.length || 0} members
                </p>
              )}
            </div>
          </div>
        </header>
      ) : (
        <TopBarChat
          participantName={displayName}
          participantCrewIds={conversation.otherParticipants?.map((p: any) => p.crewId) || []}
          participantInitials={
            conversation.isGroup
              ? displayName?.substring(0, 2).toUpperCase() || '??'
              : displayName?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || '??'
          }
          participantAvatarColor={participantAvatarColor}
          subtitle={conversation.isGroup ? `${conversation.participantIds?.length || 0} members` : undefined}
          isGroup={conversation.isGroup}
          onBack={() => router.history.back()}
        />
      )}

      {isLoading ? (
        /* Messages and the conversation are separate queries — without this
           the thread flashes "No messages yet" whenever the conversation
           resolves first. */
        <div className="flex-1 p-4">
          <ListSkeleton />
        </div>
      ) : formattedMessages.length === 0 ? (
        <div className="flex-1 p-4">
          <EmptyState
            icon={MessageSquare}
            title="No messages yet"
            description="Send your first message to start the conversation."
          />
        </div>
      ) : (
        /* Messages - scrollable container */
        <div
          ref={containerRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto overscroll-contain p-4"
        >
          <div ref={contentRef} className="flex flex-col gap-4 justify-end min-h-full">
            {formattedMessages.map((message) => {
              const row = (
                <div
                  className={`flex items-end gap-2 ${message.isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  {conversation.isGroup && !message.isCurrentUser && (
                    <Avatar className="w-8 h-8 shrink-0">
                      <AvatarFallback
                        className="text-[12px] text-background"
                        style={{ backgroundColor: message.senderAvatarColor }}
                      >
                        {message.senderInitials}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div
                    className={`flex flex-col gap-3 p-3 rounded-2xl card-shadow max-w-[273px] ${
                      message.isCurrentUser
                        ? 'bg-background-brand-subtle'
                        : 'bg-surface-L0'
                    }`}
                  >
                    {/* Sender name (group chats, non-current user only) */}
                    {conversation.isGroup && !message.isCurrentUser && (
                      <p
                        className="text-[12px] font-medium leading-normal tracking-[0.18px]"
                        style={{ color: message.senderAvatarColor }}
                      >
                        {message.senderName}
                      </p>
                    )}

                    {/* Message text — deleted messages keep the bubble but
                        show a muted placeholder instead of their content */}
                    {message.isDeleted ? (
                      <p className="flex items-center gap-2 text-content-primary-subtle text-xs italic leading-normal tracking-[0.18px]">
                        <Ban className="size-4 shrink-0" />
                        {message.isCurrentUser
                          ? 'You deleted this message'
                          : 'This message was deleted'}
                      </p>
                    ) : (
                      message.content && (
                        <p className="text-content-primary-bold text-[14px] leading-normal tracking-[0.07px] whitespace-pre-wrap wrap-break-word">
                          {message.content}
                        </p>
                      )
                    )}

                    {/* Timestamp */}
                    <p className="text-content-primary-subtle text-[12px] leading-normal tracking-[0.18px] text-right">
                      {formatTime(message.timestamp)}
                    </p>

                    {/* Attachment(s) */}
                    {(message.attachments || message.attachmentStorageId) && (
                      <AttachmentPreview
                        messageId={message.id}
                        attachmentType={message.attachmentType}
                        attachmentName={message.attachmentName}
                        attachments={message.attachments}
                      />
                    )}
                  </div>
                </div>
              );

              // Only the sender can delete their own message (the Convex
              // mutation enforces this too), so others — and already-deleted
              // messages — get a plain row.
              if (!message.isCurrentUser || message.isDeleted) {
                return <Fragment key={message.id}>{row}</Fragment>;
              }

              return (
                <SwipeableRow
                  key={message.id}
                  isOpen={openRowId === message.id}
                  onOpenChange={(o) => setOpenRowId(o ? message.id : null)}
                  onDelete={() => {
                    setConfirmDeleteId(message.id);
                    setOpenRowId(null);
                  }}
                  actionLabel="Delete"
                  variant="icon"
                  actionIcon={<Trash2 />}
                  pending={deletingId === message.id}
                  pendingLabel="Deleting..."
                  // Matches the chat background so the action chip stays
                  // hidden until the row is swiped open.
                  foregroundClassName="bg-surface-brand-L0"
                  // Breathing room so the bubble's card-shadow isn't clipped
                  // by the row's overflow-hidden.
                  className="-mx-1 px-1"
                >
                  {row}
                </SwipeableRow>
              );
            })}
          </div>
        </div>
      )}

      <AlertDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => {
          if (!o && !deletingId) setConfirmDeleteId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete message?</AlertDialogTitle>
            <AlertDialogDescription>
              This message will be removed for everyone in the conversation.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={!!deletingId}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              {deletingId ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bottom Input Area */}
      <div
        className={cn(
          'shrink-0 bg-surface-L0 border-t border-border-L2 flex flex-col gap-2 px-4 pt-4 transition-[margin-bottom,padding-bottom] duration-300 ease-out',
          // The keyboard covers the home-indicator area, so the safe-area pad
          // is redundant while it's up.
          keyboardHeight > 0 ? 'pb-4' : 'pb-[calc(var(--safe-bottom)_+_1.25rem)]',
        )}
        style={keyboardHeight > 0 ? { marginBottom: keyboardHeight } : undefined}
      >
        {/* File previews */}
        {selectedFiles.length > 0 && (
          <div className="flex flex-col gap-1">
            {isUploading && (
              <div className="flex items-center gap-2 px-2 py-2">
                <Loader2 className="w-4 h-4 text-content-brand-bold shrink-0 animate-spin" />
                <p className="text-content-primary-subtle text-[12px] leading-normal">
                  Uploading {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}...
                </p>
              </div>
            )}
            {selectedFiles.map((file, index) => (
              <div key={index} className={`bg-background-brand-subtle flex items-center gap-2 px-2 h-[52px] rounded-[10px] w-full ${isUploading ? 'opacity-50' : ''}`}>
                <FileText className="w-4 h-4 text-content-primary-subtle shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-content-primary-bold text-[14px] leading-normal tracking-[0.07px] truncate">
                    {file.name}
                  </p>
                  <p className="text-content-primary-subtle text-[12px] leading-normal tracking-[0.18px]">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="mini"
                  onClick={() => handleRemoveFile(index)}
                  disabled={isUploading}
                  className="bg-transparent p-1 h-auto"
                >
                  <X className="w-4 h-4 text-content-primary-subtle" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Input row — bottom aligned so the buttons stay put as the field grows */}
        <div className="flex items-end gap-2 w-full">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
          />

          {/* Paperclip button */}
          <Button
            type="button"
            variant="secondary"
            size="default"
            roundness="round"
            onClick={() => fileInputRef.current?.click()}
            className="shrink-0 w-9 h-9 p-2"
          >
            <Paperclip className="size-4 text-content-primary-bold" />
          </Button>

          {/* Message input */}
          <div className="flex-1 min-w-0">
            <AutosizeTextarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => {
                // Physical keyboard: Enter sends, Shift+Enter breaks the line.
                // On touch the return key is left alone so it inserts a newline
                // and only the send button sends.
                if (isDesktop && e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Message"
              maxRows={3}
            />
          </div>

          {/* Send button */}
          <Button
            onClick={handleSend}
            disabled={(!messageText.trim() && selectedFiles.length === 0) || isSending}
            variant="primary"
            size="icon"
            roundness='round'
            className="shrink-0"
          >
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
